/* =============================================================
   AUTH — SUPABASE
   =============================================================
   The app's one auth backend. Everything else imports `authClient`
   and `useAuth()` and knows nothing about Supabase.

   A session is a Supabase session PLUS the user's `profiles` row
   and company. Accounts are created by River Atlas staff, profile
   and all -- there is no public sign-up (switched off in the
   project, 2026-10-04) -- so a session without a profile is an
   account that is not set up for the app: a platform admin, or a
   user created by hand without one. Signing in with such an
   account fails with a message that says so, and an existing
   session of that kind is treated as signed out.
   ============================================================= */

import type { Session as SupabaseSession } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { AuthError } from './types'
import type { AuthClient, AuthUser, Session, UserRole } from './types'

interface ProfileRow {
  first_name: string
  last_name: string
  role: UserRole
  company_id: number
  company: { name: string } | null
}

async function loadUser(session: SupabaseSession): Promise<AuthUser | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('first_name, last_name, role, company_id, company:companies(name)')
    .eq('id', session.user.id)
    .maybeSingle()
    .overrideTypes<ProfileRow, { merge: false }>()

  if (error || !data) return null
  return {
    id: session.user.id,
    email: session.user.email ?? '',
    // Locale-ordered names are a later problem; the shell shows "First Last".
    name: `${data.first_name} ${data.last_name}`.trim(),
    company: data.company?.name ?? '',
    companyId: data.company_id,
    role: data.role,
  }
}

async function toSession(session: SupabaseSession | null): Promise<Session | null> {
  if (!session) return null
  const user = await loadUser(session)
  if (!user) return null
  return { user, expiresAt: (session.expires_at ?? 0) * 1000 }
}

export const authClient: AuthClient = {
  async getSession() {
    const { data } = await supabase.auth.getSession()
    return toSession(data.session)
  },

  async signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    if (error || !data.session) {
      // Supabase says "Invalid login credentials" for both a wrong password
      // and an unknown email, which is right: say nothing about which.
      throw new AuthError('Wrong email or password.', 'invalid_credentials')
    }
    const session = await toSession(data.session)
    if (!session) {
      await supabase.auth.signOut()
      throw new AuthError(
        'This account is not set up for the app yet. Contact River Atlas to finish setting it up.',
        'no_profile',
      )
    }
    return session
  },

  async signOut() {
    await supabase.auth.signOut()
  },

  onAuthStateChange(callback) {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // INITIAL_SESSION is what getSession() already returned on boot, and
      // TOKEN_REFRESHED does not change who is signed in. Re-reading the
      // profile on every hourly refresh would be a wasted round trip.
      if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') return
      // Supabase holds a lock while this callback runs; awaiting another
      // Supabase call inside it deadlocks. Defer to the next task.
      setTimeout(() => void toSession(session).then(callback), 0)
    })
    return () => data.subscription.unsubscribe()
  },
}
